import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { getUserFrequencyChallengeMetrics, getMarianaTekReservations, getMarianaTekClassSession, getFYSUserProgress, formatFYSProgress, lookupFYSPrize, updateFYSPrize, triggerChallengeEvent } from '../services/api'
import ChallengeHelpTicketModal from './ChallengeHelpTicketModal'
import UwbStatusCard from './UwbStatusCard'
import { useAuth } from './auth/AuthProvider'

const Container = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
`

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin: 0;
`

const HelpButton = styled.button`
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 4px;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover {
    background: #b8002d;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`

const ColumnsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`

const ColumnSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`

const ColumnTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${theme.color.border.light};
`

const ChallengeCard = styled.div`
  background: ${theme.color.background.frame};
  border: 2px solid ${theme.color.accent.primary};
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 8px ${theme.color.shadow.light};
`

const ChallengeTitle = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.color.accent.primary};
  margin-bottom: 1.5rem;
  text-align: center;
`

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`

const ProgressLabel = styled.div`
  font-size: 1.4rem;
  color: ${theme.color.text.grey};
  font-weight: 600;
  text-align: center;
`

const ProgressValue = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: ${theme.color.accent.primary};
  text-align: center;
`

const TierBadge = styled.div`
  background: ${theme.color.accent.primary};
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 20px;
  font-size: 1.4rem;
  font-weight: 600;
  text-align: center;
  margin-top: 1rem;
`

const NoDataMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${theme.color.text.grey};
  font-size: 1.6rem;
  font-style: italic;
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.color.border.light};
  border-top: 4px solid ${theme.color.accent.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 3rem auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const ErrorMessage = styled.div`
  background: #fee;
  border: 1px solid #fcc;
  color: #c33;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`

const CompletedBadge = styled.div`
  background: #4caf50;
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.6rem;
  font-weight: 700;
  text-align: center;
  margin: 1rem 0;
`

const ProgressBarOuter = styled.div`
  width: 100%;
  height: 12px;
  background-color: ${theme.color.border.light};
  border-radius: 6px;
  overflow: hidden;
  margin: 1rem 0;
`

const ProgressBarInner = styled.div<{ $percentage: number }>`
  width: ${props => props.$percentage || 0}%;
  height: 100%;
  background: linear-gradient(90deg, ${theme.color.accent.primary} 0%, #ff6b4a 100%);
  border-radius: 6px;
  transition: width 0.5s ease-out;
`

const ProgressFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.4rem;
  color: ${theme.color.text.grey};
  margin-top: 0.5rem;
`

const PrizeSection = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${theme.color.border.light};
`

const PrizeTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1rem;
`

const PrizeDetail = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  font-size: 1.3rem;

  & > span:first-child {
    color: ${theme.color.text.grey};
    font-weight: 600;
  }

  & > span:last-child {
    color: ${theme.color.text.base};
    font-weight: 500;
  }
`

const PrizeImage = styled.img`
  width: 100%;
  max-width: 300px;
  border-radius: 8px;
  margin: 1rem auto;
  display: block;
`

const UpgradeBadge = styled.div`
  background: #fff3cd;
  color: #856404;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
  margin-top: 0.5rem;
`

const TriggerSection = styled.div`
  margin-top: 2rem;
  padding: 2rem;
  background: ${theme.color.background.frame};
  border: 2px solid #ff9800;
  border-radius: 12px;
`

const TriggerTitle = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const TriggerButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`

const TriggerButton = styled.button<{ $variant?: string }>`
  background: ${props => props.$variant === '20' ? '#d32f2f' : '#ff9800'};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 4px;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`

const StudioModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const StudioModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2.5rem;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`

const StudioModalTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1.5rem;
`

const StudioSelect = styled.select`
  width: 100%;
  padding: 1rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1.4rem;
  margin-bottom: 1.5rem;
  background: white;

  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }
`

const StudioModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`

const ModalButton = styled.button<{ $primary?: boolean }>`
  padding: 0.8rem 2rem;
  border-radius: 4px;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  border: ${props => props.$primary ? 'none' : `2px solid ${theme.color.border.light}`};
  background: ${props => props.$primary ? theme.color.accent.primary : 'white'};
  color: ${props => props.$primary ? 'white' : theme.color.text.base};

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const TriggerFeedback = styled.div<{ $type: 'success' | 'error' }>`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 6px;
  font-size: 1.3rem;
  font-weight: 600;
  background: ${props => props.$type === 'success' ? '#e8f5e9' : '#fee'};
  color: ${props => props.$type === 'success' ? '#2e7d32' : '#c33'};
  border: 1px solid ${props => props.$type === 'success' ? '#c8e6c9' : '#fcc'};
`

const EditButton = styled.button`
  background: none;
  border: 1px solid ${theme.color.accent.primary};
  color: ${theme.color.accent.primary};
  padding: 0.4rem 1rem;
  border-radius: 4px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  margin-left: 1rem;

  &:hover {
    background: ${theme.color.accent.primary};
    color: white;
  }
`

const EditForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`

const EditField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`

const EditLabel = styled.label`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${theme.color.text.grey};
`

const EditSelect = styled.select`
  padding: 0.6rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1.3rem;
  background: white;

  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }
`

const EditInput = styled.input`
  padding: 0.6rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1.3rem;
  background: white;

  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }
`

const EditButtonRow = styled.div`
  display: flex;
  gap: 0.8rem;
  margin-top: 0.5rem;
`

const SaveButton = styled.button`
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  padding: 0.6rem 1.5rem;
  border-radius: 4px;
  font-size: 1.3rem;
  font-weight: 600;
  cursor: pointer;

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const CancelButton = styled.button`
  background: white;
  color: ${theme.color.text.base};
  border: 2px solid ${theme.color.border.light};
  padding: 0.6rem 1.5rem;
  border-radius: 4px;
  font-size: 1.3rem;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: ${theme.color.background.light}; }
`

const PRIZE_CHOICES = [
  '$40 Gift Card',
  '$100 Gift Card',
  'Premium Hoodie',
  'Premium Water Bottle',
  'Upgrade to the 20 Class Track',
]

const HOODIE_STYLES = ['Hoodie Choice 1', 'Hoodie Choice 2']
const HOODIE_COLORS = ['Black', 'White', 'Grey']
const HOODIE_SIZES = ['XS', 'Small', 'Medium', 'Large', 'XL', 'XXL']
const BOTTLE_STYLES = ['Water Bottle Choice 1', 'Water Bottle Choice 2']
const TRACK_CHOICES = ['12 Class Track', '20 Class Track']

const STUDIOS = [
  { name: 'Arlington, VA', id: 9657 },
  { name: 'Austin, TX', id: 9733 },
  { name: 'Aventura, FL', id: 9631 },
  { name: 'Back Bay, MA', id: 9632 },
  { name: 'Boca Raton, FL', id: 10231 },
  { name: 'Brentwood, CA', id: 9654 },
  { name: 'Brooklyn Heights, NY', id: 10030 },
  { name: 'Buckhead, GA', id: 9627 },
  { name: 'Burlingame, CA', id: 9641 },
  { name: 'Castro, CA', id: 9640 },
  { name: 'Charleston, SC', id: 10099 },
  { name: 'Charlotte, NC', id: 9651 },
  { name: 'Chelsea, NY', id: 9594 },
  { name: 'Chestnut Hill, MA', id: 9621 },
  { name: 'Dallas, TX', id: 9628 },
  { name: 'Denver, CO', id: 9766 },
  { name: 'Downtown Boston, MA', id: 9626 },
  { name: 'Dupont Circle, DC', id: 9629 },
  { name: 'East 64th, NY', id: 9598 },
  { name: 'East 86th, NY', id: 9865 },
  { name: 'FiDi, CA', id: 9613 },
  { name: 'Hoboken, NJ', id: 10132 },
  { name: 'Hollywood, CA', id: 9600 },
  { name: 'Houston, TX', id: 9649 },
  { name: 'Lincoln Park, IL', id: 9618 },
  { name: 'Long Beach, CA', id: 9652 },
  { name: 'Marina, CA', id: 9619 },
  { name: 'Miami Beach, FL', id: 9616 },
  { name: 'Midtown Miami, FL', id: 9615 },
  { name: 'Minneapolis, MN', id: 9661 },
  { name: 'Nashville, TN', id: 9620 },
  { name: 'Navy Yard, DC', id: 10098 },
  { name: 'Newport Beach, CA', id: 10096 },
  { name: 'Noho, NY', id: 9604 },
  { name: 'Palo Alto, CA', id: 9642 },
  { name: 'Park Ave South, NY', id: 9601 },
  { name: 'Pasadena, CA', id: 9658 },
  { name: 'Philadelphia, PA', id: 9656 },
  { name: 'Portland, OR', id: 9898 },
  { name: 'River North, IL', id: 9608 },
  { name: 'Roslyn, NY', id: 9663 },
  { name: 'Salt Lake City, UT', id: 10165 },
  { name: 'San Diego, CA', id: 9599 },
  { name: 'Santa Monica, CA', id: 9668 },
  { name: 'Santana Row, CA', id: 9655 },
  { name: 'Scarsdale, NY', id: 9606 },
  { name: 'Scottsdale, AZ', id: 10063 },
  { name: 'Seattle, WA', id: 9643 },
  { name: 'South Bay, CA', id: 9633 },
  { name: 'Southampton, NY', id: 9610 },
  { name: 'Studio City, CA', id: 9674 },
  { name: 'Tampa, FL', id: 9964 },
  { name: 'Tribeca, NY', id: 9603 },
  { name: 'Upper West Side, NY', id: 9630 },
  { name: 'Venice, CA', id: 9609 },
  { name: 'Wainscott, NY', id: 9597 },
  { name: 'Wall Street, NY', id: 10233 },
  { name: 'West Hollywood, CA', id: 9595 },
  { name: 'Wicker Park, IL', id: 9662 },
  { name: 'Williamsburg, NY', id: 10133 },
  { name: "King's Cross, London", id: 9832 },
  { name: 'Canary Wharf, London', id: 9837 },
  { name: 'London East', id: 9834 },
  { name: 'London Soho', id: 9840 },
  { name: 'London SW1', id: 9836 },
  { name: 'London West', id: 9835 },
  { name: "St. Paul's, London", id: 9833 },
  { name: 'Manchester', id: 9838 },
  { name: 'Liverpool', id: 9931 },
  { name: 'Dublin', id: 10134 },
  { name: 'Calgary', id: 9669 },
  { name: 'Richmond, Toronto', id: 9671 },
  { name: 'Vancouver', id: 9670 },
  { name: 'Yorkville, Toronto', id: 9672 },
  { name: 'Zurich', id: 10198 },
  { name: 'Berlin', id: 10268 },
  { name: 'Frankfurt', id: 10267 },
  { name: 'Copenhagen', id: 10269 },
  { name: 'Barcelona', id: 10273 },
  { name: 'Madrid', id: 10274 },
  { name: 'Paris', id: 10266 },
  { name: 'Oslo (Barcode)', id: 10270 },
  { name: 'Oslo (Colosseum)', id: 10265 },
  { name: 'Bergen (Fana)', id: 10272 },
  { name: 'Bergen', id: 10299 },
  { name: 'Gothenburg', id: 10271 },
  { name: 'Stockholm', id: 10275 },
  { name: 'Milan (Senato)', id: 10264 },
]

const FYS_CHALLENGE_ID = '5b576f16-a862-4f8d-82da-8a8188512783'
const FYS_CHALLENGE_NAME = 'Find Your Strength 2026'
const SPRINT_12_ID = 'ef3bd0d7-20ef-4b37-8edd-0bb6d4a809ad'
const SPRINT_20_ID = '9ff403ca-bb2b-4560-89ca-4dda95192839'

// Hell Week 2026 challenge config
const HELL_WEEK_US = {
  challengeId: '2d4d158b-70df-4e23-8030-97a9126f5594',
  sprintId: 'ff551135-c274-4727-be3a-c11ae0d249b3',
  name: 'Hell Week 2026',
}
const HELL_WEEK_UK = {
  challengeId: 'ff2a9268-2338-4093-999e-b597f430d684',
  sprintId: '2f9342f2-6cd0-4f40-bb85-13ad3825def4',
  name: 'Hell Week 2026 (UK/Ireland)',
}

// UK/Dublin studio names for region detection
const UK_DUBLIN_STUDIOS = [
  'london', 'finsbury', 'central st giles', 'canada water', 'aldgate',
  'canary wharf', 'soho', "st paul", 'sw1', 'east', 'west', 'central',
  'liverpool', 'manchester', 'dublin',
]

function getPrizeImageUrl(prize: any): string | null {
  if (!prize) return null

  if (prize.prize_choice === 'Premium Hoodie' && prize.hoodie_choice) {
    const isChoice1 = prize.hoodie_choice.includes('1')
    const color = (prize.hoodie_color || 'black').toLowerCase()

    if (color.includes('white')) {
      return isChoice1 ? '/images/prizes/hoodie-white-choice1.png' : '/images/prizes/hoodie-white-choice2.png'
    } else if (color.includes('grey') || color.includes('gray')) {
      return isChoice1 ? '/images/prizes/hoodie-grey-choice1.png' : '/images/prizes/hoodie-grey-choice2.png'
    } else {
      // Default to black
      return isChoice1 ? '/images/prizes/hoodie-black-choice1.jpg' : '/images/prizes/hoodie-black-choice2.jpg'
    }
  }

  if (prize.prize_choice === 'Premium Water Bottle' && prize.water_bottle_choice) {
    const isChoice1 = prize.water_bottle_choice.includes('1')
    return isChoice1 ? '/images/prizes/bottle-choice1.jpg' : '/images/prizes/bottle-choice2.jpg'
  }

  return null
}

interface ChallengeDataProps {
  userData: any
  reservationsData?: any
  marianaTekData?: any
}

const ChallengeData: React.FC<ChallengeDataProps> = ({ userData, reservationsData, marianaTekData }) => {
  const { isAdmin, isSuperAdmin } = useAuth()
  const [challengeMetrics, setChallengeMetrics] = useState<any>(null)
  const [actualClassCount, setActualClassCount] = useState<number | null>(null)
  const [fysData, setFysData] = useState<any>(null)
  const [fysPrize, setFysPrize] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const [showHelpTicketModal, setShowHelpTicketModal] = useState(false)
  const [showStudioModal, setShowStudioModal] = useState(false)
  const [selectedStudio, setSelectedStudio] = useState<string>('')
  const [triggerType, setTriggerType] = useState<'12' | '20' | null>(null)
  const [triggerLoading, setTriggerLoading] = useState(false)
  const [triggerFeedback, setTriggerFeedback] = useState<{ type: 'success' | 'error', message: string } | null>(null)
  const [isEditingPrize, setIsEditingPrize] = useState(false)
  const [editPrize, setEditPrize] = useState<any>({})
  const [editSaving, setEditSaving] = useState(false)
  const [editFeedback, setEditFeedback] = useState<{ type: 'success' | 'error', message: string } | null>(null)

  // Hell Week 2026 state
  const [hellWeekData, setHellWeekData] = useState<any>(null)
  const [hellWeekRegistered, setHellWeekRegistered] = useState<boolean | null>(null)
  const [isCompletingHellWeek, setIsCompletingHellWeek] = useState(false)
  const [completeHellWeekFeedback, setCompleteHellWeekFeedback] = useState<{ type: 'success' | 'error', message: string } | null>(null)

  // Determine if user is UK/Dublin based on home_studio custom attribute
  const homeStudio = (userData?.user?.custom_attributes?.home_studio || '').toLowerCase()
  const isUkOrDublin = UK_DUBLIN_STUDIOS.some(s => homeStudio.includes(s))
  const hellWeekConfig = isUkOrDublin ? HELL_WEEK_UK : HELL_WEEK_US

  const fetchHellWeekData = async () => {
    const email = userData?.user?.email
    if (!email) return
    try {
      const response = await fetch(
        `/api/hell-week/registration?email=${encodeURIComponent(email)}`
      )
      if (response.ok) {
        const data = await response.json()
        setHellWeekRegistered(data.registered)
        setHellWeekData(data.registration || null)
      } else {
        setHellWeekRegistered(false)
      }
    } catch (err) {
      console.log('Hell Week lookup failed:', err)
      setHellWeekRegistered(null)
    }
  }

  const handleCompleteHellWeek = async () => {
    if (!userData?.user?.id) {
      setCompleteHellWeekFeedback({ type: 'error', message: 'No SessionM user ID found' })
      return
    }
    setIsCompletingHellWeek(true)
    setCompleteHellWeekFeedback(null)
    try {
      await triggerChallengeEvent(userData.user.id, {
        'challenge.completed': {
          qty: 1,
          context: {
            challenge_id: hellWeekConfig.challengeId,
            challenge_name: hellWeekConfig.name,
            sprint_id: hellWeekConfig.sprintId,
            studio_id: hellWeekData?.studio_id,
          }
        }
      })
      setCompleteHellWeekFeedback({ type: 'success', message: 'Challenge completion event sent successfully!' })
    } catch (err) {
      setCompleteHellWeekFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to send completion event' })
    } finally {
      setIsCompletingHellWeek(false)
    }
  }

  useEffect(() => {
    const fetchChallengeData = async () => {
      if (!userData?.user?.external_id) {
        setError('No Mariana Tek ID found for this user')
        return
      }

      // Demo mode: For j.kliman@barrys.com, show demo challenge data
      if (userData?.user?.email === 'j.kliman@barrys.com') {
        console.log('🎬 Challenge Demo Mode: Generating demo challenge for j.kliman@barrys.com')
        setChallengeMetrics({
          tier: '50',
          tierNumber: 50,
          label: '42 / 50',
          optInDate: '2024-10-01T00:00:00Z'
        })
        setActualClassCount(42)
        // Demo FYS data
        setFysData({
          challengeName: 'Find Your Strength 2026',
          sprintName: '20 Class Track',
          sprintGoal: 20,
          classesCompleted: 8,
          progressPercentage: 40,
          isCompleted: false,
          classesRemaining: 12
        })
        setFysPrize(null)
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError('')

      // Fetch Red Room Records data
      try {
        const metrics = await getUserFrequencyChallengeMetrics(userData.user.external_id)
        setChallengeMetrics(metrics)

        console.log('Challenge metrics:', metrics)
        console.log('Reservations data:', reservationsData)

        if (reservationsData && metrics?.optInDate) {
          console.log('Calculating class count from opt-in date:', metrics.optInDate)
          const count = await calculateActualClassCount(reservationsData, metrics.optInDate)
          console.log('Calculated class count:', count)
          setActualClassCount(count)
        } else {
          setActualClassCount(null)
        }
      } catch (err) {
        console.log('Red Room Records data not available:', err)
        setChallengeMetrics(null)
      }

      // Fetch Find Your Strength 2026 data
      try {
        const fysProgress = await getFYSUserProgress(userData.user.external_id)
        console.log('FYS progress:', fysProgress)
        const formattedFys = formatFYSProgress(fysProgress)
        console.log('Formatted FYS:', formattedFys)
        setFysData(formattedFys)
      } catch (fysErr) {
        console.log('User not enrolled in FYS challenge:', fysErr)
        setFysData(null)
      }

      // Fetch FYS prize selection from Athena (independent of other fetches)
      if (userData?.user?.email) {
        try {
          const prizeResult = await lookupFYSPrize(userData.user.email)
          console.log('FYS prize result:', prizeResult)
          if (prizeResult.found) {
            setFysPrize(prizeResult.prizeSelection)
          } else {
            setFysPrize(null)
          }
        } catch (prizeErr) {
          console.log('FYS prize lookup failed:', prizeErr)
          setFysPrize(null)
        }
      }

      // Fetch Hell Week 2026 registration status
      await fetchHellWeekData()

      setIsLoading(false)
    }

    fetchChallengeData()
  }, [userData?.user?.external_id, reservationsData])

  const calculateActualClassCount = async (reservationsData: any, optInDate: string): Promise<number> => {
    try {
      const reservationsList = reservationsData.data || []
      console.log('Total reservations to process:', reservationsList.length)

      const optInDateTime = new Date(optInDate).getTime()
      console.log('Opt-in date:', optInDate, 'timestamp:', optInDateTime)

      // Challenge ends November 1st, 2025
      const challengeEndDate = new Date('2025-11-01T23:59:59')
      const challengeEndDateTime = challengeEndDate.getTime()
      console.log('Challenge end date:', challengeEndDate, 'timestamp:', challengeEndDateTime)

      // Filter for checked-in classes within challenge period (opt-in date to November 1st)
      const validReservations = reservationsList.filter((reservation: any) => {
        const status = reservation.attributes?.status?.toLowerCase() || ''

        // Must be checked in
        if (status !== 'check in') {
          return false
        }

        // Must be within challenge period (after opt-in date, before November 1st)
        const checkInDate = reservation.attributes?.check_in_date
        if (checkInDate) {
          const checkInDateTime = new Date(checkInDate).getTime()
          if (checkInDateTime < optInDateTime || checkInDateTime > challengeEndDateTime) {
            return false
          }
        } else {
          return false
        }

        return true
      })

      console.log('Valid checked-in reservations in challenge period:', validReservations.length)

      return validReservations.length
    } catch (err) {
      console.error('Failed to calculate actual class count:', err)
      return 0
    }
  }

  const handleTriggerClick = (type: '12' | '20') => {
    setTriggerType(type)
    setSelectedStudio('')
    setShowStudioModal(true)
    setTriggerFeedback(null)
  }

  const handleTriggerConfirm = async () => {
    if (!selectedStudio || !triggerType || !userData?.user?.id) return

    setTriggerLoading(true)
    setTriggerFeedback(null)

    const sprintId = triggerType === '12' ? SPRINT_12_ID : SPRINT_20_ID
    const studioId = parseInt(selectedStudio)
    const studioName = STUDIOS.find(s => s.id === studioId)?.name || ''

    const events = {
      'challenge.completed': {
        qty: 1,
        context: {
          challenge_id: FYS_CHALLENGE_ID,
          challenge_name: FYS_CHALLENGE_NAME,
          sprint_id: sprintId,
          studio_id: studioId
        }
      }
    }

    try {
      await triggerChallengeEvent(userData.user.id, events)
      setTriggerFeedback({
        type: 'success',
        message: `${triggerType} Class Completion triggered successfully for ${studioName}`
      })
      setShowStudioModal(false)
    } catch (err: any) {
      setTriggerFeedback({
        type: 'error',
        message: `Failed to trigger: ${err.message}`
      })
    } finally {
      setTriggerLoading(false)
    }
  }

  const handleEditPrize = () => {
    setEditPrize({
      prize_choice: fysPrize?.prize_choice || '',
      hoodie_choice: fysPrize?.hoodie_choice || '',
      hoodie_color: fysPrize?.hoodie_color || '',
      sizing: fysPrize?.sizing || '',
      water_bottle_choice: fysPrize?.water_bottle_choice || '',
      challenge_track: fysPrize?.challenge_track || '',
      address: fysPrize?.address || '',
      address_line2: fysPrize?.address_line2 || '',
      city: fysPrize?.city || '',
      state: fysPrize?.state || '',
      zip_code: fysPrize?.zip_code || '',
      country: fysPrize?.country || '',
    })
    setIsEditingPrize(true)
    setEditFeedback(null)
  }

  const handleSavePrize = async () => {
    if (!userData?.user?.email) return

    setEditSaving(true)
    setEditFeedback(null)

    try {
      const needsAddress = editPrize.prize_choice === 'Premium Hoodie' || editPrize.prize_choice === 'Premium Water Bottle'
      await updateFYSPrize({
        email: userData.user.email,
        first_name: userData.user.first_name || fysPrize?.first_name || '',
        last_name: userData.user.last_name || fysPrize?.last_name || '',
        prize_choice: editPrize.prize_choice,
        hoodie_choice: editPrize.prize_choice === 'Premium Hoodie' ? editPrize.hoodie_choice : '',
        hoodie_color: editPrize.prize_choice === 'Premium Hoodie' ? editPrize.hoodie_color : '',
        sizing: editPrize.prize_choice === 'Premium Hoodie' ? editPrize.sizing : '',
        water_bottle_choice: editPrize.prize_choice === 'Premium Water Bottle' ? editPrize.water_bottle_choice : '',
        challenge_track: editPrize.challenge_track,
        address: needsAddress ? editPrize.address : '',
        address_line2: needsAddress ? editPrize.address_line2 : '',
        city: needsAddress ? editPrize.city : '',
        state: needsAddress ? editPrize.state : '',
        zip_code: needsAddress ? editPrize.zip_code : '',
        country: needsAddress ? editPrize.country : '',
      })

      setEditFeedback({ type: 'success', message: 'Prize updated successfully' })
      setIsEditingPrize(false)

      // Refresh prize data after a short delay for Athena to discover new partition
      setTimeout(async () => {
        try {
          const prizeResult = await lookupFYSPrize(userData.user.email)
          if (prizeResult.found) {
            setFysPrize(prizeResult.prizeSelection)
          }
        } catch (err) {
          console.log('Prize refresh failed:', err)
        }
      }, 3000)
    } catch (err: any) {
      setEditFeedback({ type: 'error', message: `Failed to save: ${err.message}` })
    } finally {
      setEditSaving(false)
    }
  }

  const prizeEditForm = isAdmin && isEditingPrize ? (
    <EditForm>
      <EditField>
        <EditLabel>Prize Choice</EditLabel>
        <EditSelect value={editPrize.prize_choice} onChange={e => setEditPrize({ ...editPrize, prize_choice: e.target.value })}>
          <option value="">-- Select --</option>
          {PRIZE_CHOICES.map(p => <option key={p} value={p}>{p}</option>)}
        </EditSelect>
      </EditField>

      {editPrize.prize_choice === 'Premium Hoodie' && (
        <>
          <EditField>
            <EditLabel>Style</EditLabel>
            <EditSelect value={editPrize.hoodie_choice} onChange={e => setEditPrize({ ...editPrize, hoodie_choice: e.target.value })}>
              <option value="">-- Select --</option>
              {HOODIE_STYLES.map(s => <option key={s} value={s}>{s}</option>)}
            </EditSelect>
          </EditField>
          <EditField>
            <EditLabel>Color</EditLabel>
            <EditSelect value={editPrize.hoodie_color} onChange={e => setEditPrize({ ...editPrize, hoodie_color: e.target.value })}>
              <option value="">-- Select --</option>
              {HOODIE_COLORS.map(c => <option key={c} value={c}>{c}</option>)}
            </EditSelect>
          </EditField>
          <EditField>
            <EditLabel>Size</EditLabel>
            <EditSelect value={editPrize.sizing} onChange={e => setEditPrize({ ...editPrize, sizing: e.target.value })}>
              <option value="">-- Select --</option>
              {HOODIE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </EditSelect>
          </EditField>
        </>
      )}

      {editPrize.prize_choice === 'Premium Water Bottle' && (
        <EditField>
          <EditLabel>Style</EditLabel>
          <EditSelect value={editPrize.water_bottle_choice} onChange={e => setEditPrize({ ...editPrize, water_bottle_choice: e.target.value })}>
            <option value="">-- Select --</option>
            {BOTTLE_STYLES.map(s => <option key={s} value={s}>{s}</option>)}
          </EditSelect>
        </EditField>
      )}

      {(editPrize.prize_choice === 'Premium Hoodie' || editPrize.prize_choice === 'Premium Water Bottle') && (
        <>
          <EditLabel style={{ marginTop: '0.5rem', fontSize: '1.3rem', fontWeight: 700 }}>Shipping Address</EditLabel>
          <EditField>
            <EditLabel>Address</EditLabel>
            <EditInput value={editPrize.address} onChange={e => setEditPrize({ ...editPrize, address: e.target.value })} placeholder="Street address" />
          </EditField>
          <EditField>
            <EditLabel>Address Line 2</EditLabel>
            <EditInput value={editPrize.address_line2} onChange={e => setEditPrize({ ...editPrize, address_line2: e.target.value })} placeholder="Apt, suite, etc. (optional)" />
          </EditField>
          <EditField>
            <EditLabel>City</EditLabel>
            <EditInput value={editPrize.city} onChange={e => setEditPrize({ ...editPrize, city: e.target.value })} placeholder="City" />
          </EditField>
          <EditField>
            <EditLabel>State / Province</EditLabel>
            <EditInput value={editPrize.state} onChange={e => setEditPrize({ ...editPrize, state: e.target.value })} placeholder="State or province" />
          </EditField>
          <EditField>
            <EditLabel>Zip / Postal Code</EditLabel>
            <EditInput value={editPrize.zip_code} onChange={e => setEditPrize({ ...editPrize, zip_code: e.target.value })} placeholder="Zip or postal code" />
          </EditField>
          <EditField>
            <EditLabel>Country</EditLabel>
            <EditInput value={editPrize.country} onChange={e => setEditPrize({ ...editPrize, country: e.target.value })} placeholder="Country" />
          </EditField>
        </>
      )}

      <EditField>
        <EditLabel>Challenge Track</EditLabel>
        <EditSelect value={editPrize.challenge_track} onChange={e => setEditPrize({ ...editPrize, challenge_track: e.target.value })}>
          <option value="">-- Select --</option>
          {TRACK_CHOICES.map(t => <option key={t} value={t}>{t}</option>)}
        </EditSelect>
      </EditField>

      <EditButtonRow>
        <SaveButton disabled={editSaving || !editPrize.prize_choice} onClick={handleSavePrize}>
          {editSaving ? 'Saving...' : 'Save'}
        </SaveButton>
        <CancelButton onClick={() => setIsEditingPrize(false)}>Cancel</CancelButton>
      </EditButtonRow>
    </EditForm>
  ) : null

  const hasAddress = fysPrize && (fysPrize.address || fysPrize.city || fysPrize.state || fysPrize.zip_code || fysPrize.country)
  const addressDisplay = hasAddress ? (
    <>
      <PrizeDetail>
        <span>Address:</span>
        <span>
          {[fysPrize.address, fysPrize.address_line2].filter(Boolean).join(', ')}
          {fysPrize.city || fysPrize.state || fysPrize.zip_code ? <br /> : null}
          {[fysPrize.city, fysPrize.state].filter(Boolean).join(', ')}{fysPrize.zip_code ? ` ${fysPrize.zip_code}` : ''}
          {fysPrize.country ? <><br />{fysPrize.country}</> : null}
        </span>
      </PrizeDetail>
    </>
  ) : null

  // Reusable Hell Week card
  const hellWeekCard = (
    <ChallengeCard>
      <ChallengeTitle>🔥 Hell Week 2026</ChallengeTitle>
      {hellWeekRegistered === null ? (
        <NoDataMessage style={{ padding: '1rem', fontSize: '1.3rem' }}>Checking registration...</NoDataMessage>
      ) : hellWeekRegistered && hellWeekData ? (
        <>
          <CompletedBadge style={{ background: '#e6ffe6', color: '#008000' }}>Registered</CompletedBadge>
          <ProgressContainer>
            <ProgressLabel>Classes Completed</ProgressLabel>
            <ProgressValue>
              {hellWeekData.classes_completed || 0}
              {hellWeekData.classes_goal ? ` / ${hellWeekData.classes_goal}` : ''}
            </ProgressValue>
          </ProgressContainer>
          {hellWeekData.studio_name && (
            <ProgressContainer>
              <ProgressLabel>Studio</ProgressLabel>
              <ProgressValue>{hellWeekData.studio_name}</ProgressValue>
            </ProgressContainer>
          )}
          {hellWeekData.sprint_name && <TierBadge>{hellWeekData.sprint_name}</TierBadge>}
          {hellWeekData.completed_at ? (
            <CompletedBadge>Completed {new Date(hellWeekData.completed_at).toLocaleDateString()}</CompletedBadge>
          ) : isSuperAdmin ? (
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                onClick={handleCompleteHellWeek}
                disabled={isCompletingHellWeek}
                style={{
                  background: isCompletingHellWeek ? '#999' : '#CC0000',
                  color: 'white',
                  border: 'none',
                  padding: '0.6rem 1.5rem',
                  borderRadius: '4px',
                  fontSize: '1.3rem',
                  fontWeight: 700,
                  cursor: isCompletingHellWeek ? 'wait' : 'pointer',
                }}
              >
                {isCompletingHellWeek ? 'Sending...' : 'Complete Challenge'}
              </button>
            </div>
          ) : null}
          {completeHellWeekFeedback && (
            <div style={{
              marginTop: '0.75rem',
              padding: '0.6rem',
              borderRadius: '4px',
              background: completeHellWeekFeedback.type === 'success' ? '#e6ffe6' : '#ffe6e6',
              color: completeHellWeekFeedback.type === 'success' ? '#008000' : '#d70036',
              fontSize: '1.2rem',
              textAlign: 'center',
            }}>
              {completeHellWeekFeedback.message}
            </div>
          )}
        </>
      ) : (
        <>
          <CompletedBadge style={{ background: '#ffe6e6', color: '#d70036' }}>Not Registered</CompletedBadge>
        </>
      )}
    </ChallengeCard>
  )

  // Reusable FYS card (for Previous Campaigns)
  const fysCard = fysData ? (
    <ChallengeCard>
      <ChallengeTitle>💪 {fysData.challengeName}</ChallengeTitle>
      <ProgressContainer>
        <ProgressLabel>Progress</ProgressLabel>
        <ProgressValue>{fysData.classesCompleted} / {fysData.sprintGoal}</ProgressValue>
      </ProgressContainer>
      {fysData.isCompleted ? (
        <CompletedBadge>Challenge Complete!</CompletedBadge>
      ) : (
        <ProgressBarOuter><ProgressBarInner $percentage={fysData.progressPercentage} /></ProgressBarOuter>
      )}
      <TierBadge>{fysData.sprintName}</TierBadge>
      {fysPrize && <ProgressLabel>Prize: {fysPrize.prize_choice || 'Not selected'}</ProgressLabel>}
      {isAdmin && fysPrize && <EditButton onClick={handleEditPrize}>Edit Prize</EditButton>}
      {prizeEditForm}
      {editFeedback && <ProgressLabel>{editFeedback.message}</ProgressLabel>}
    </ChallengeCard>
  ) : null

  const hasPreviousCampaigns = challengeMetrics || fysData

  if (isLoading) {
    return (
      <Container>
        <TitleContainer>
          <Title>Challenge Data</Title>
          {isAdmin && <HelpButton onClick={() => setShowHelpTicketModal(true)}>
            Challenge Help Ticket
          </HelpButton>}
        </TitleContainer>
        <LoadingSpinner />
        <ChallengeHelpTicketModal
          isOpen={showHelpTicketModal}
          onClose={() => setShowHelpTicketModal(false)}
          userData={userData}
        />
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <TitleContainer>
          <Title>Challenge Data</Title>
          {isAdmin && <HelpButton onClick={() => setShowHelpTicketModal(true)}>
            Challenge Help Ticket
          </HelpButton>}
        </TitleContainer>
        <ErrorMessage>{error}</ErrorMessage>
        <ChallengeHelpTicketModal
          isOpen={showHelpTicketModal}
          onClose={() => setShowHelpTicketModal(false)}
          userData={userData}
        />
      </Container>
    )
  }

  if (!challengeMetrics) {
    return (
      <Container>
        <TitleContainer>
          <Title>Challenge Data</Title>
          {isAdmin && <HelpButton onClick={() => setShowHelpTicketModal(true)}>
            Challenge Help Ticket
          </HelpButton>}
        </TitleContainer>
        <UwbStatusCard email={userData?.user?.email} marianaTekData={marianaTekData} />
        <ColumnsContainer>
          <ColumnSection>
            <ColumnTitle>Previous Campaigns</ColumnTitle>
            {hellWeekCard}
            {fysCard}
          </ColumnSection>
        </ColumnsContainer>
        <ChallengeHelpTicketModal
          isOpen={showHelpTicketModal}
          onClose={() => setShowHelpTicketModal(false)}
          userData={userData}
        />
      </Container>
    )
  }

  console.log('Rendering ChallengeData - actualClassCount:', actualClassCount, 'challengeMetrics:', challengeMetrics)

  return (
    <Container>
      <TitleContainer>
        <Title>Challenge Data</Title>
        {isAdmin && <HelpButton onClick={() => setShowHelpTicketModal(true)}>
          Challenge Help Ticket
        </HelpButton>}
      </TitleContainer>
      <UwbStatusCard email={userData?.user?.email} marianaTekData={marianaTekData} />
      <ColumnsContainer>
        <ColumnSection>
          <ColumnTitle>Previous Campaigns</ColumnTitle>
          {hellWeekCard}
          <ChallengeCard>
            <ChallengeTitle>💿 Red Room Records</ChallengeTitle>
            <ProgressContainer>
              <ProgressLabel>Progress</ProgressLabel>
              <ProgressValue>
                {actualClassCount !== null ? `${actualClassCount} / ${challengeMetrics.tierNumber}` : challengeMetrics.label}
              </ProgressValue>
            </ProgressContainer>
            <TierBadge>
              {challengeMetrics.tier} Class Challenge
            </TierBadge>
          </ChallengeCard>
          {fysCard}
        </ColumnSection>
      </ColumnsContainer>
      <ChallengeHelpTicketModal
        isOpen={showHelpTicketModal}
        onClose={() => setShowHelpTicketModal(false)}
        userData={userData}
      />
    </Container>
  )
}

export default ChallengeData

